import {Injectable} from '@angular/core';
import {
  addDoc,
  collection,
  collectionData,
  deleteDoc,
  deleteField,
  doc,
  DocumentData,
  FieldValue,
  Firestore,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  QueryDocumentSnapshot,
  setDoc,
  Timestamp,
  where,
} from '@angular/fire/firestore';
import {combineLatest, from, Observable, of} from 'rxjs';
import {IStore} from '../../shared-models/store/store';
import {
  path_operational_stores_data_auto_ordering_auto_orders_GET,
  path_operational_stores_data_auto_ordering_auto_orders_order_items_GET,
} from '../database-paths';
import {catchError, map} from 'rxjs/operators';
import {
  IAutoOrderFirestoreOrder,
  IAutoOrderFirestoreOrderItem,
  IAutoOrderSupplierEmails,
} from '../../../features/auto-ordering/models';
import {IFirebaseQuery} from '../../shared-models/firebase/firebase-queries';
import {
  encodeStrict,
  firebaseTimeStampToDate,
  generateFirebaseCollectionReference,
} from '../../shared-utils/firestore/firestore.utils';
import {FirestoreDataConverter} from 'firebase/firestore/lite';

type WithId<T> = T & {id: string};

@Injectable({
  providedIn: 'root',
})
export class CollectionOperationalAutoOrderingService {

  constructor(private firestore: Firestore) {
  }

  async setDocument<DataType>(
    path: string,
    data: DataType,
  ): Promise<void> {
    try {
      const docRef = doc(this.firestore, path);
      await setDoc(docRef, data, {merge: true});
    } catch (error) {
      console.error(error);
    }
  }

  async getDocument<DataType>(
    path: string,
  ): Promise<DataType> {
    try {
      const docRef = doc(this.firestore, path);
      const docSnapshot = await getDoc(docRef);
      if (docSnapshot.exists()) {
        return docSnapshot.data() as DataType;
      } else {
        return {} as DataType;
      }
    } catch (error) {
      console.error(error);
    }
  }

  async deleteDocument<DataType>(
    path: string,
  ): Promise<void> {
    try {
      const docRef = doc(this.firestore, path);
      await deleteDoc(docRef);
    } catch (error) {
      console.error(`Error deleting document at path: ${path}\n`, error);
    }
  }

  /**
   * Removes a single field from a document, leaving the rest of the document untouched.
   *
   * @param {string} path - Firestore document path.
   * @param {keyof DataType | string} fieldName - Name of the field to remove.
   * @throws Rethrows the Firestore error so callers can react to a failed delete.
   */
  async deleteField<DataType>(path: string, fieldName: keyof DataType | string): Promise<void> {
    try {
      const docRef = doc(this.firestore, path);
      const fieldDeletion: Record<string, FieldValue> = {[String(fieldName)]: deleteField()};
      await setDoc(docRef, fieldDeletion, {merge: true});
    } catch (error) {
      console.error(`Error deleting field '${String(fieldName)}' at path: ${path}\n`, error);
      throw error;
    }
  }

  getAutoOrders(store: IStore): Observable<IAutoOrderFirestoreOrder[]> {
    const collectionRef = collection(this.firestore, path_operational_stores_data_auto_ordering_auto_orders_GET(store.storeId));
    return collectionData(collectionRef, {idField: 'id'})
      .pipe(map((docs: IAutoOrderFirestoreOrder[]) => {
        const autoOrderOrders: IAutoOrderFirestoreOrder[] = [];
        docs.forEach((data: IAutoOrderFirestoreOrder): void => {
          autoOrderOrders.push({
            ...data,
            generated: firebaseTimeStampToDate(data.generated as unknown as Timestamp),
          });
        });
        return autoOrderOrders;
      }));
  }

  // Get only retrieves data once
  getAutoOrdersFirebaseQueriesGet(store: IStore, firebaseQueries: IFirebaseQuery[]): Observable<IAutoOrderFirestoreOrder[]> {
    const collectionRef = collection(this.firestore, path_operational_stores_data_auto_ordering_auto_orders_GET(store.storeId));
    const queryRef = generateFirebaseCollectionReference(firebaseQueries, collectionRef);
    return from(
      getDocs(queryRef)
        .then((snapshot) => {
          const autoOrderOrders: IAutoOrderFirestoreOrder[] = [];
          snapshot.forEach((doc) => {
            const data = doc.data() as IAutoOrderFirestoreOrder;
            autoOrderOrders.push({
              ...data,
              generated: firebaseTimeStampToDate(data.generated as unknown as Timestamp),
            });
          });
          return autoOrderOrders;
        }),
    );
  }

  getAutoOrdersFirebaseQueries(store: IStore, firebaseQueries: IFirebaseQuery[]): Observable<IAutoOrderFirestoreOrder[]> {
    const collectionRef = collection(this.firestore, path_operational_stores_data_auto_ordering_auto_orders_GET(store.storeId));
    const queryRef = generateFirebaseCollectionReference(firebaseQueries, collectionRef);
    return new Observable((observer) => {
      const unsubscribe = onSnapshot(queryRef, (snapshot) => {
        const autoOrderOrders: IAutoOrderFirestoreOrder[] = [];
        snapshot.forEach((doc) => {
          const data = doc.data() as IAutoOrderFirestoreOrder;
          let errorCast = null;

          if (data?.errorMsg) {
            if (typeof data.errorMsg === 'string') {
              try {
                const message = data.errorMsg.replace(/'([^']*)'/g, '"$1"');
                errorCast = JSON.parse(data.errorMsg.replace(/'([^']*)'/g, '"$1"'));
              } catch {
                errorCast = data.errorMsg;
              }
            } else {
              errorCast = data.errorMsg;
            }
          }
          autoOrderOrders.push({
            ...data,
            purchaseOrderNo: data?.purchaseOrderNo ? data.purchaseOrderNo : 'Not Submitted',
            generated: firebaseTimeStampToDate(data.generated as unknown as Timestamp),
            executed: firebaseTimeStampToDate(data.executed as unknown as Timestamp),
            error: errorCast,
          });
        });
        observer.next(autoOrderOrders);
      }, (error) => {
        observer.error(error);
      });

      return () => {
        unsubscribe();
      };
    });
  }

  //{'error': -1, 'desc': 'Some issues were found with your order. Some items have been placed on hold for purchase orders since you created the order.', 'itemErrors': {'10120378': {'error': -1, 'desc': 'Item on hold'}}}
  //{"error": -1, "desc": "Some issues were found with your order. Some items have been placed on hold for purchase orders since you created the order.", "itemErrors": {"10120378": {"error": -1, "desc": "Item on hold"}}}

  getAutoOrdersQueriesNoInStatus(store: IStore, firebaseQuery1: IFirebaseQuery[], firebaseQuery2: IFirebaseQuery[]): Observable<IAutoOrderFirestoreOrder[]> {
    const collectionRef = collection(this.firestore, path_operational_stores_data_auto_ordering_auto_orders_GET(store.storeId));
    const query1 = generateFirebaseCollectionReference(firebaseQuery1, collectionRef);
    const query2 = generateFirebaseCollectionReference(firebaseQuery2, collectionRef);
    return from(
      Promise.all([getDocs(query1), getDocs(query2)]).then(([snapshot1, snapshot2]) => {
        const autoOrderOrders: IAutoOrderFirestoreOrder[] = [];
        snapshot1.forEach((doc) => {
          const data = doc.data() as IAutoOrderFirestoreOrder;
          autoOrderOrders.push({
            ...data,
            generated: firebaseTimeStampToDate(data.generated as unknown as Timestamp),
          });
        });
        snapshot2.forEach((doc) => {
          const data = doc.data() as IAutoOrderFirestoreOrder;
          autoOrderOrders.push({
            ...data,
            generated: firebaseTimeStampToDate(data.generated as unknown as Timestamp),
          });
        });
        const uniqueOrders = Array.from(
          new Map(autoOrderOrders.map((order: IAutoOrderFirestoreOrder) => [order.orderId, order])).values(),
        );
        return uniqueOrders;
      }),
    );
  }

  getAutoOrderOrderItems(
    store: IStore,
    orderId: string,
  ): Observable<IAutoOrderFirestoreOrderItem[]> {
    const path = path_operational_stores_data_auto_ordering_auto_orders_order_items_GET(
      store.storeId,
      orderId,
    );
    const itemConv = <T>(): FirestoreDataConverter<WithId<T>> => ({
      toFirestore: (data: WithId<T>): DocumentData => {
        const {id, ...rest} = data;
        return rest as DocumentData;
      },
      fromFirestore: (snap: QueryDocumentSnapshot): WithId<T> => {
        return {id: snap.id, ...(snap.data() as T)};
      },
    });

    const colRef = collection(this.firestore, path).withConverter(
      itemConv<IAutoOrderFirestoreOrderItem>(),
    );

    return collectionData(colRef).pipe(
      map((docs: WithId<IAutoOrderFirestoreOrderItem>[]) =>
        docs.map(({id, ...rest}) => ({
          ...rest,
          code: decodeURIComponent(id || ''),
        })),
      ),
    );
  }

  getSupplierEmails(
    path: string,
    supplierIds: string[],
  ): Observable<Record<string, {
    selected: string[],
    additional: string[],
  }>> {
    if (supplierIds?.length === 0) {
      return of({});
    }
    const validSupplierIds = supplierIds.filter((id: string) => id).map((id: string) => encodeStrict(id));
    const queries: Observable<(DocumentData | (DocumentData & {
      id: string;
    }))[]>[] = [];
    while (validSupplierIds.length > 0) {
      const deleteCount = validSupplierIds.length >= 10 ? 10 : validSupplierIds.length;
      const chunk = validSupplierIds.splice(0, deleteCount);
      const collectionRef = collection(this.firestore, path);
      const q = query(collectionRef, where('__name__', 'in', chunk));
      const queryObservable = collectionData(q, {idField: 'id'});
      queries.push(queryObservable);
    }
    return combineLatest(queries).pipe(
      map((results: IAutoOrderSupplierEmails[][]) => {
        const allDocs = ([] as IAutoOrderSupplierEmails[]).concat(...results);
        const supplierEmails: Record<string, {
          selected: string[],
          additional: string[],
        }> = {};
        supplierIds.forEach((id: string) => {
          supplierEmails[id] = {} as {
            selected: string[],
            additional: string[],
          };

          const found = allDocs.find((doc: IAutoOrderSupplierEmails) => doc.id === encodeStrict(id));
          if (found?.selected?.length > 0) {
            supplierEmails[id].selected = found.selected;
          } else {
            supplierEmails[id].selected = [];
          }
          if (found?.additional?.length > 0) {
            supplierEmails[id].additional = found.additional;
          } else {
            supplierEmails[id].additional = [];
          }
        });
        return supplierEmails;
      }),
      catchError((error) => {
        console.error('Error fetching supplier emails:', error);
        return of({});
      }),
    );
  }

  async addDocument<DataType>(
    collectionPath: string,
    data: DataType,
  ): Promise<string> {
    try {
      const collectionRef = collection(this.firestore, collectionPath);
      const docRef = await addDoc(collectionRef, data);

      return docRef.id;
    } catch (error) {
      console.error('Error adding document:', error);
      throw error;
    }
  }

}
